import { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

/**
 * Four independent corner radii in user-space px, in order top-left, top-right,
 * bottom-right, bottom-left. Structurally identical to the manager's
 * `PlaceholderCornerRadii` — kept as a separate type here so the editor core
 * does not depend on a consuming feature module (unidirectional rule).
 */
export interface RectCornerRadii {
  tl: number;
  tr: number;
  br: number;
  bl: number;
}

export interface RectCornersFacade {
  /**
   * Set (or, with `undefined`, clear) per-corner radii on a native rect. Writes
   * `data.cornerRadii`, invalidates the object cache (native rects cache to an
   * offscreen canvas, so a data-only change would not repaint), records exactly
   * one history step, and repaints. Pass `undefined` to return the rect to its
   * stock uniform `rx`/`ry` rendering.
   */
  set(target: fabric.Rect, radii: RectCornerRadii | undefined): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    rectCorners?: RectCornersFacade;
  }
}

type RectRenderFn = (this: fabric.Object, ctx: CanvasRenderingContext2D) => void;
type PatchedRender = RectRenderFn & { __rectCornersOriginal?: RectRenderFn };
/** `_renderPaintInOrder` is an internal Fabric method with an unconstrained `this`. */
type CtxRenderFn = (ctx: CanvasRenderingContext2D) => void;

/**
 * Reads validated per-corner radii from a rect's `data.cornerRadii`, or
 * `undefined` when absent/malformed — in which case the rect renders through
 * Fabric's stock uniform `rx`/`ry` path. Absence is the deliberate "uniform
 * mode" tri-state (mirrors the image placeholder convention).
 */
export function readRectCornerRadii(rect: fabric.Object): RectCornerRadii | undefined {
  const radii = (rect as { data?: { cornerRadii?: Partial<RectCornerRadii> } }).data?.cornerRadii;
  if (!radii) return undefined;
  const { tl, tr, br, bl } = radii;
  if ([tl, tr, br, bl].every((v) => typeof v === 'number' && Number.isFinite(v))) {
    return { tl: tl as number, tr: tr as number, br: br as number, bl: bl as number };
  }
  return undefined;
}

/**
 * Traces a `width`×`height` rounded-rect path with four independent corner radii
 * in the rect's LOCAL coordinate space (Fabric renders a rect centered on its
 * origin, so x ∈ [-w/2, w/2]). Each radius is clamped to `min(w,h)/2` so adjacent
 * rounded corners never cross; a radius of `0` yields a sharp corner. Mirrors the
 * manager's Phase-1 `roundedRectPath`, but emits Canvas 2D path commands rather
 * than an SVG `d` string. `arcTo` matches Fabric's own rounded-rect geometry so a
 * uniform per-corner radius reads identically to a stock `rx`/`ry` rect.
 */
export function traceRoundedRectPath(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  radii: RectCornerRadii,
): void {
  const max = Math.min(width, height) / 2;
  const clamp = (v: number) => Math.max(0, Math.min(v, max));
  const tl = clamp(radii.tl);
  const tr = clamp(radii.tr);
  const br = clamp(radii.br);
  const bl = clamp(radii.bl);
  const x = -width / 2;
  const y = -height / 2;
  const right = x + width;
  const bottom = y + height;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(right - tr, y);
  ctx.arcTo(right, y, right, y + tr, tr);
  ctx.lineTo(right, bottom - br);
  ctx.arcTo(right, bottom, right - br, bottom, br);
  ctx.lineTo(x + bl, bottom);
  ctx.arcTo(x, bottom, x, bottom - bl, bl);
  ctx.lineTo(x, y + tl);
  ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}

/**
 * Adds per-corner corner radii to native `fabric.Rect` objects.
 *
 * Render: patches `fabric.Rect.prototype._render` once at install. A rect with
 * valid `data.cornerRadii` is drawn as a four-corner rounded path and painted via
 * Fabric's own `_renderPaintInOrder` (so fill, stroke, `strokeUniform`,
 * `paintFirst`, and shadow all behave exactly as for a stock rect — only the path
 * geometry is replaced). A rect without `data.cornerRadii` falls through to the
 * original stock render unchanged.
 *
 * The patch is on the Fabric singleton prototype, so it affects every rect in the
 * app — but the `data.cornerRadii` presence check means untouched rects hit the
 * unchanged original path, and `dispose()` restores the stashed original so a
 * disposed editor leaves no global side effect. Double-install is impossible
 * within one editor (closed-set plugin-name registry); across editors the stashed
 * `__rectCornersOriginal` prevents wrapper stacking.
 *
 * Façade (`editor.rectCorners`): the one entry point UI/strategies use to write
 * per-corner radii, because writing `data.cornerRadii` alone does not invalidate
 * the cached rect (it would not repaint) — see `set`.
 */
export function rectCornersPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.RECT_CORNERS,
    install(editor: FabricEditor) {
      const proto = fabric.Rect.prototype as unknown as { _render: PatchedRender };
      const current = proto._render;
      // The true stock render: if a prior install already wrapped it, reuse the
      // stashed original so we never stack wrappers (StrictMode double-mount,
      // sequential editors, the offscreen export editor).
      const original: RectRenderFn = current?.__rectCornersOriginal ?? current;
      const patched: PatchedRender = function (this: fabric.Object, ctx: CanvasRenderingContext2D) {
        const radii = readRectCornerRadii(this);
        if (!radii) {
          original.call(this, ctx);
          return;
        }
        const rect = this as fabric.Rect;
        traceRoundedRectPath(ctx, rect.width ?? 0, rect.height ?? 0, radii);
        (this as unknown as { _renderPaintInOrder: CtxRenderFn })._renderPaintInOrder(ctx);
      };
      patched.__rectCornersOriginal = original;
      proto._render = patched;

      editor.rectCorners = {
        set(target: fabric.Rect, radii: RectCornerRadii | undefined): void {
          // Native rects cache to an offscreen canvas; a data-only write does not
          // mark the cache dirty, so force it before the repaint that setData
          // requests — otherwise the new geometry would not appear until an
          // unrelated redraw.
          target.dirty = true;
          // setData emits object:modified → the history plugin records exactly one
          // undo step. Deliberately NOT wrapped in history.transaction (which
          // suspends recording and would drop the step).
          editor.objects.setData(target, 'cornerRadii', radii);
        },
      };
    },
    dispose(editor: FabricEditor) {
      const proto = fabric.Rect.prototype as unknown as { _render: PatchedRender };
      const original = proto._render?.__rectCornersOriginal;
      if (original) proto._render = original;
      editor.rectCorners = undefined;
    },
  };
}
